import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import GameAPI from '../Game/GameAPI';
import {
  isCollectingAction,
  resourceNodeName,
} from '../Game/Gathering/gathering';
import { MEDIA_VARIANTS } from '../../media';
import useImageCache from '../../hooks/useImageCache';
import { useTileHelpers } from './GameWorldContext';
import {
  CAMERA_SAFE_ZONE_RATIO,
  calculateCameraOffset,
  calculateMapTransform,
  clampTilePosition,
  filterMapItemsInViewport,
  getCenteredHitBounds,
  hitTestInteractiveObjects,
  moveKeyboardCursor,
  screenPointToTile,
} from './mapRenderModel';
import './TileMap.css';

export { CAMERA_SAFE_ZONE_RATIO, calculateCameraOffset };

export const MOVEMENT_STEP_DURATION_MS = 1000;
export const MAX_ONLINE_PLAYERS_PER_TILE = 3;
export const CAMERA_TILT_DEGREES = 30;
export const WORLD_SPRITE_PITCH_DEGREES = 75;

export function resolveEnemyLevelBadge(enemyLevel, playerLevel) {
  const difference = Math.max(1, Math.round(Number(enemyLevel) || 1))
    - Math.max(1, Math.round(Number(playerLevel) || 1));
  if (difference <= -3) {
    return { color: '#928a77', label: 'no experience reward' };
  }
  if (difference < 0) {
    return { color: '#82c75b', label: 'lower level, experience reward' };
  }
  if (difference === 0) {
    return { color: '#f2ead7', label: 'same level' };
  }
  if (difference === 1) {
    return { color: '#d9c54a', label: 'one level above' };
  }
  if (difference === 2) {
    return { color: '#d8953f', label: 'two levels above' };
  }
  return { color: '#d95a4f', label: 'three or more levels above' };
}

const DEFAULT_DESTINATION_OPTIONS = {
  arrivalAction: 'RESTING',
  stopWithinDistance: 0,
};

const OBJECT_PRIORITY = {
  camp: 600,
  exit: 590,
  starter: 500,
  champion: 450,
  roaming: 425,
  worldLoot: 400,
  groundLoot: 300,
  resource: 200,
};

export function groupOnlinePlayerMarkers(players) {
  const groups = new Map();
  (Array.isArray(players) ? players : []).forEach(player => {
    const key = `${player.x}:${player.y}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(player);
  });

  return [...groups.values()].flatMap(group => {
    const visible = group.slice(0, MAX_ONLINE_PLAYERS_PER_TILE);
    const population = Math.max(
      group.length,
      ...group.map(player => player.tilePopulation || 1),
    );
    return visible.map((player, index) => ({
      ...player,
      hiddenCount: index === visible.length - 1
        ? Math.max(0, population - visible.length)
        : 0,
      horizontalOffset: (index - ((visible.length - 1) / 2)) * 10,
    }));
  });
}

export function supportsWebGL2() {
  if (
    typeof document === 'undefined'
    || typeof window === 'undefined'
    || typeof window.WebGL2RenderingContext === 'undefined'
  ) return false;
  try {
    return Boolean(document.createElement('canvas').getContext('webgl2'));
  } catch (_error) {
    return false;
  }
}

function useViewportDimensions(containerRef) {
  const [viewport, setViewport] = useState({ height: 0, width: 0 });

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;
    const update = () => {
      const { height, width } = element.getBoundingClientRect();
      setViewport(current => (
        current.width === width && current.height === height
          ? current
          : { height, width }
      ));
    };
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update);
      observer.observe(element);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [containerRef]);

  return viewport;
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(() => (
    typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  ));
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return undefined;
    const update = () => setReduced(query.matches);
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

function objectBounds(object, tileSize, visualSize = tileSize) {
  return getCenteredHitBounds({
    height: visualSize,
    minSize: 44,
    tileSize,
    width: visualSize,
    x: object.x,
    y: object.y,
  });
}

export default function TileMap() {
  const {
    avatarSelection,
    campEntrance,
    championBusyPlacementId,
    draggedItem,
    getTile,
    isWalkable,
    map,
    mapImageRevision,
    movementPending,
    movementPath,
    movementTarget,
    onActivateCampEntrance,
    onActivateChampion,
    onActivateRegionExit,
    onDropInventoryItem,
    onOpenGroundLoot,
    onOpenStarterCache,
    onOpenWorldLoot,
    onRequestWorldIntent,
    onConsumeWorldIntent,
    onClearWorldIntent,
    onSelectMovementTarget,
    playerAction,
    playerLevel,
    playerPosition,
    regionExits,
    regionWorldState,
    queuedWorldIntent,
    scavengeRadius,
    starterCache,
    tileSize,
    travelAllowed = true,
    travelUnavailableReason = '',
  } = useTileHelpers();
  const regionWorldStateRef = useRef(regionWorldState);
  regionWorldStateRef.current = regionWorldState;
  const stageRef = useRef(null);
  const gridRef = useRef(null);
  const viewport = useViewportDimensions(stageRef);
  const reducedMotion = useReducedMotion();
  const [cameraOffset, setCameraOffset] = useState({ x: 0, y: 0 });
  const [focusedObjectId, setFocusedObjectId] = useState(null);
  const [hoveredObjectId, setHoveredObjectId] = useState(null);
  const [keyboardCursor, setKeyboardCursor] = useState(playerPosition);
  const [pendingInteraction, setPendingInteraction] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  const [rendererKey, setRendererKey] = useState(0);
  const [RendererComponent, setRendererComponent] = useState(null);
  const [rendererState, setRendererState] = useState(() => (
    supportsWebGL2() ? 'loading' : 'unsupported'
  ));
  const [rendererError, setRendererError] = useState('');
  const collectingLocked = isCollectingAction(playerAction);
  const fetchMapImage = useCallback(
    (imageId, variant) => GameAPI.fetchImage(imageId, variant),
    [],
  );
  const {
    getImageUrl: getMapImageUrl,
    revision: mapObjectImageRevision,
  } = useImageCache(fetchMapImage, { returnState: true });

  useEffect(() => {
    let active = true;
    if (!supportsWebGL2()) {
      setRendererState('unsupported');
      setRendererComponent(null);
      return undefined;
    }
    setRendererState('loading');
    setRendererError('');
    import('./WebGLWorldRenderer')
      .then(module => {
        if (active) setRendererComponent(() => module.default);
      })
      .catch(error => {
        if (!active) return;
        setRendererError(error?.message || 'The renderer could not be loaded.');
        setRendererState('error');
      });
    return () => { active = false; };
  }, [rendererKey]);

  useEffect(() => {
    setKeyboardCursor(current => clampTilePosition(
      current,
      map.width,
      map.height,
    ));
  }, [map.height, map.width]);

  const selectTarget = useCallback((
    x,
    y,
    options = DEFAULT_DESTINATION_OPTIONS,
  ) => {
    if (!movementPending && onSelectMovementTarget) {
      return onSelectMovementTarget(x, y, options);
    }
    return null;
  }, [movementPending, onSelectMovementTarget]);

  const requestOrActivate = useCallback(async (intent, activate) => {
    const executeNow = onRequestWorldIntent
      ? await onRequestWorldIntent(intent)
      : true;
    return executeNow ? activate() : null;
  }, [onRequestWorldIntent]);

  const selectDestination = useCallback((x, y, options) => {
    setPendingInteraction(null);
    return requestOrActivate({
      id: `tile:${x}:${y}`,
      key: `tile:${x}:${y}`,
      kind: 'tile',
      label: `Tile ${x}, ${y}`,
      owner: 'map',
      x,
      y,
    }, () => selectTarget(x, y, options));
  }, [requestOrActivate, selectTarget]);

  const moveThenActivate = useCallback(async (
    target,
    activate,
    { confirmNearbyWithServer = false } = {},
  ) => {
    if (!target || typeof activate !== 'function') {
      return null;
    }
    const isNearby = (
      Math.abs(playerPosition.x - target.x)
      + Math.abs(playerPosition.y - target.y)
    ) <= 1;
    if (isNearby && !confirmNearbyWithServer) {
      setPendingInteraction(null);
      return activate();
    }

    const movementResult = await selectTarget(
      target.x,
      target.y,
      { arrivalAction: 'RESTING', stopWithinDistance: 1 },
    );
    if (!movementResult) return null;
    if (Number(movementResult.pathLength) === 0) {
      setPendingInteraction(null);
      return activate();
    }
    setPendingInteraction({
      activate,
      routeObserved: false,
      x: target.x,
      y: target.y,
    });
    return movementResult;
  }, [
    playerPosition.x,
    playerPosition.y,
    selectTarget,
  ]);

  useEffect(() => {
    if (!pendingInteraction) return;
    const action = String(playerAction || '').toUpperCase();
    const targetMatches = movementTarget?.x === pendingInteraction.x
      && movementTarget?.y === pendingInteraction.y;
    if (targetMatches) {
      if (!pendingInteraction.routeObserved) {
        setPendingInteraction(current => current && ({
          ...current,
          routeObserved: true,
        }));
      }
      return;
    }
    if (movementTarget) {
      setPendingInteraction(null);
      return;
    }
    if (!pendingInteraction.routeObserved || action === 'MOVING') return;

    const isNearby = (
      Math.abs(playerPosition.x - pendingInteraction.x)
      + Math.abs(playerPosition.y - pendingInteraction.y)
    ) <= 1;
    setPendingInteraction(null);
    if (isNearby && (!action || ['IDLE', 'RESTING'].includes(action))) {
      Promise.resolve(pendingInteraction.activate()).catch(error => {
        console.error('Failed to activate the selected map object', error);
      });
    }
  }, [
    movementTarget,
    pendingInteraction,
    playerAction,
    playerPosition.x,
    playerPosition.y,
  ]);

  const canDropOnTile = useCallback((x, y) => (
    !collectingLocked
    && draggedItem?.fromComponent === 'inventory'
    && Math.abs(playerPosition.x - x) + Math.abs(playerPosition.y - y) <= 1
    && isWalkable?.(x, y)
  ), [
    collectingLocked,
    draggedItem?.fromComponent,
    isWalkable,
    playerPosition.x,
    playerPosition.y,
  ]);

  const activateGroundLootNow = useCallback(summary => moveThenActivate(
    summary,
    () => onOpenGroundLoot?.(summary),
    { confirmNearbyWithServer: Boolean(onSelectMovementTarget) },
  ), [moveThenActivate, onOpenGroundLoot, onSelectMovementTarget]);

  const activateGroundLoot = useCallback(summary => requestOrActivate({
    id: `ground-loot:${summary.containerId}`,
    key: `ground-loot:${summary.containerId}`,
    kind: 'groundLoot',
    label: `${summary.tier} treasure pack`,
    owner: 'map',
    x: summary.x,
    y: summary.y,
  }, () => activateGroundLootNow(summary)), [
    activateGroundLootNow,
    requestOrActivate,
  ]);

  const activateWorldLootNow = useCallback(summary => moveThenActivate(
    summary,
    () => onOpenWorldLoot?.(summary),
    { confirmNearbyWithServer: Boolean(onSelectMovementTarget) },
  ), [moveThenActivate, onOpenWorldLoot, onSelectMovementTarget]);

  const activateWorldLoot = useCallback(summary => requestOrActivate({
    id: `world-loot:${summary.containerId}`,
    key: `world-loot:${summary.containerId}`,
    kind: 'worldLoot',
    label: summary.name || 'Loot container',
    owner: 'map',
    x: summary.x,
    y: summary.y,
  }, () => activateWorldLootNow(summary)), [
    activateWorldLootNow,
    requestOrActivate,
  ]);

  const activateStarterCacheNow = useCallback(summary => moveThenActivate(
    summary,
    () => onOpenStarterCache?.(summary),
  ), [moveThenActivate, onOpenStarterCache]);

  const activateStarterCache = useCallback(summary => requestOrActivate({
    id: `starter:${summary.cacheId}`,
    key: `starter:${summary.cacheId}`,
    kind: 'starter',
    label: 'Starter Cache',
    owner: 'map',
    x: summary.x,
    y: summary.y,
  }, () => activateStarterCacheNow(summary)), [
    activateStarterCacheNow,
    requestOrActivate,
  ]);

  const activateChampionNow = useCallback(champion => {
    const follow = current => moveThenActivate(
      current,
      () => {
        const latest = regionWorldStateRef.current?.champion;
        if (
          latest?.placementId === current.placementId
          && (latest.x !== current.x || latest.y !== current.y)
        ) {
          return follow(latest);
        }
        return onActivateChampion?.(latest || current);
      },
      { confirmNearbyWithServer: Boolean(onSelectMovementTarget) },
    );
    return follow(champion);
  }, [moveThenActivate, onActivateChampion, onSelectMovementTarget]);

  const activateChampion = useCallback(champion => requestOrActivate({
    id: `champion:${champion.placementId}`,
    key: `champion:${champion.placementId}`,
    kind: 'champion',
    label: champion.name || 'Region Champion',
    owner: 'map',
    x: champion.x,
    y: champion.y,
  }, () => activateChampionNow(champion)), [
    activateChampionNow,
    requestOrActivate,
  ]);

  const activateRoamingEnemy = useCallback(enemy => requestOrActivate({
    id: `roaming:${enemy.instanceId}`,
    key: `roaming:${enemy.instanceId}`,
    kind: 'roaming',
    label: enemy.name || 'Enemy',
    owner: 'map',
    x: enemy.x,
    y: enemy.y,
  }, () => selectTarget(enemy.x, enemy.y)), [
    requestOrActivate,
    selectTarget,
  ]);

  const activateResourceNodeNow = useCallback(node => {
    if (node.status !== 'available') return null;
    return selectTarget(node.x, node.y, {
      arrivalAction: 'SCAVENGING',
      scavengeTargetPlacementId: node.placementId,
      stopWithinDistance: 1,
    });
  }, [
    selectTarget,
  ]);

  const activateResourceNode = useCallback(node => requestOrActivate({
    id: `resource:${node.placementId}`,
    key: `resource:${node.placementId}`,
    kind: 'resource',
    label: resourceNodeName(node),
    owner: 'map',
    x: node.x,
    y: node.y,
  }, () => activateResourceNodeNow(node)), [
    activateResourceNodeNow,
    requestOrActivate,
  ]);

  const tiltScaleY = Math.cos(CAMERA_TILT_DEGREES * (Math.PI / 180));

  useEffect(() => {
    setCameraOffset(currentOffset => calculateCameraOffset({
      currentOffset,
      mapHeight: map.height,
      mapWidth: map.width,
      playerPosition,
      tileSize,
      viewportHeight: viewport.height / tiltScaleY,
      viewportWidth: viewport.width,
    }));
  }, [
    map.height,
    map.width,
    playerPosition,
    tileSize,
    tiltScaleY,
    viewport.height,
    viewport.width,
  ]);

  const transform = useMemo(() => calculateMapTransform({
    cameraOffset,
    mapHeight: map.height,
    mapWidth: map.width,
    tileSize,
    verticalScaleY: tiltScaleY,
    viewportHeight: viewport.height,
    viewportWidth: viewport.width,
  }), [
    cameraOffset,
    map.height,
    map.width,
    tileSize,
    tiltScaleY,
    viewport.height,
    viewport.width,
  ]);

  const terrainRows = useMemo(() => Array.from(
    { length: map.height },
    (_, y) => Array.from({ length: map.width }, (_, x) => (
      getTile(x, y) || {
        base: {
          fallbackColor: '#6b7280',
          key: 'missing',
          walkable: false,
        },
        detail: null,
      }
    )),
  // The revision is the settled authenticated-texture signal for this map.
  ), [getTile, map.height, map.width, mapImageRevision]); // eslint-disable-line react-hooks/exhaustive-deps

  const details = useMemo(() => terrainRows.flatMap((row, y) => (
    row.flatMap((tile, x) => tile.detail ? [{
      ...tile.detail,
      id: `${y}:${x}:${tile.detail.id || tile.detail.key || 'detail'}`,
      renderScaleTiles: tile.detail.renderScaleTiles || 1,
      renderWidthTiles: tile.detail.renderWidthTiles
        || tile.detail.renderScaleTiles
        || 1,
      renderHeightTiles: tile.detail.renderHeightTiles
        || tile.detail.renderScaleTiles
        || 1,
      x,
      y,
    }] : [])
  )), [terrainRows]);

  const visibleDetails = useMemo(() => filterMapItemsInViewport(details, {
    tileSize,
    transform,
  }), [details, tileSize, transform]);

  const interactiveObjects = useMemo(() => {
    // This value only exists to refresh cached object image URLs after media settles.
    void mapObjectImageRevision;
    const result = [];
    if (campEntrance) {
      const disabled = movementPending || !travelAllowed;
      result.push({
        ...campEntrance,
        activate: () => {
          setPendingInteraction(null);
          return onActivateCampEntrance?.(campEntrance);
        },
        bounds: objectBounds(campEntrance, tileSize, 54),
        depth: 9,
        disabled,
        disabledReason: !travelAllowed
          ? travelUnavailableReason
          : 'Another request is in progress.',
        id: 'camp-entrance',
        kind: 'camp',
        label: `Camp entrance at ${campEntrance.x}, ${campEntrance.y}${!travelAllowed ? `, unavailable: ${travelUnavailableReason}` : ''}`,
        priority: OBJECT_PRIORITY.camp,
        visualSize: 54,
      });
    }

    (regionExits || []).forEach(exit => {
      const disabled = movementPending
        || !travelAllowed
        || exit.enabled === false;
      result.push({
        ...exit,
        activate: () => onActivateRegionExit?.(exit),
        bounds: objectBounds(exit, tileSize, 54),
        depth: 9,
        disabled,
        disabledReason: exit.enabled === false
          ? `${exit.label} is currently closed.`
          : !travelAllowed
            ? travelUnavailableReason
            : 'Another request is in progress.',
        id: `exit:${exit.key}`,
        kind: 'exit',
        label: `${exit.label} region exit at ${exit.x}, ${exit.y}, ${exit.direction || 'passage'}${exit.enabled === false ? ', disabled' : !travelAllowed ? `, unavailable: ${travelUnavailableReason}` : ''}`,
        priority: OBJECT_PRIORITY.exit,
        visualSize: 54,
      });
    });

    if (starterCache) {
      result.push({
        ...starterCache,
        activate: () => activateStarterCache(starterCache),
        bounds: objectBounds(starterCache, tileSize, 44),
        count: starterCache.slotCount,
        depth: 8,
        disabled: !travelAllowed,
        disabledReason: travelUnavailableReason,
        execute: () => activateStarterCacheNow(starterCache),
        id: `starter:${starterCache.cacheId}`,
        kind: 'starter',
        label: `Private Starter Cache at ${starterCache.x}, ${starterCache.y}, ${starterCache.slotCount} item${starterCache.slotCount === 1 ? '' : 's'}`,
        priority: OBJECT_PRIORITY.starter,
        visualSize: 44,
      });
    }

    const champion = regionWorldState?.champion;
    if (champion) {
      const activating = championBusyPlacementId === champion.placementId;
      const name = champion.name || 'Region Champion';
      const kind = champion.kind === 'boss' ? 'boss' : 'champion';
      const behavior = champion.isAggressive ? 'aggressive' : 'passive';
      const level = Math.max(1, Number(champion.level) || 1);
      const levelBadge = resolveEnemyLevelBadge(level, playerLevel);
      result.push({
        ...champion,
        activate: () => activateChampion(champion),
        bounds: objectBounds(champion, tileSize, 64),
        depth: 8,
        disabled: !travelAllowed
          || activating,
        disabledReason: !travelAllowed
          ? travelUnavailableReason
          : `${name} challenge is already starting.`,
        execute: () => activateChampionNow(champion),
        id: `champion:${champion.placementId}`,
        imageUrl: getMapImageUrl(
          champion.imageId,
          MEDIA_VARIANTS.ENTITY_ICON,
        ),
        kind: 'champion',
        label: `${name}, level ${level}, ${levelBadge.label}, ${behavior} region ${kind} at ${champion.x}, ${champion.y}`,
        levelBadgeColor: levelBadge.color,
        priority: OBJECT_PRIORITY.champion,
        visualSize: 64,
      });
    }

    (regionWorldState?.roamingEnemies || []).forEach(enemy => {
      const behavior = enemy.isAggressive ? 'aggressive' : 'passive';
      const pursuing = enemy.isPursuingViewer ? ', pursuing you' : '';
      const levelBadge = resolveEnemyLevelBadge(enemy.level, playerLevel);
      result.push({
        ...enemy,
        activate: () => activateRoamingEnemy(enemy),
        bounds: objectBounds(enemy, tileSize, 58),
        count: enemy.isPursuingViewer ? '!' : null,
        depth: 8,
        disabled: movementPending || !travelAllowed,
        disabledReason: !travelAllowed
          ? travelUnavailableReason
          : 'Another movement request is in progress.',
        execute: () => selectTarget(enemy.x, enemy.y),
        id: `roaming:${enemy.instanceId}`,
        imageUrl: getMapImageUrl(
          enemy.imageId,
          MEDIA_VARIANTS.ENTITY_ICON,
        ),
        kind: 'roaming',
        label: `${enemy.name}, level ${enemy.level}, ${levelBadge.label}, ${behavior} enemy at ${enemy.x}, ${enemy.y}${pursuing}`,
        levelBadgeColor: levelBadge.color,
        priority: OBJECT_PRIORITY.roaming,
        visualSize: 58,
      });
    });

    (regionWorldState?.worldLootContainers || []).forEach(summary => {
      const empty = summary.status === 'empty';
      const visualSize = Math.max(32, tileSize * summary.renderScaleTiles);
      const imageId = empty && summary.emptyImageId
        ? summary.emptyImageId
        : summary.closedImageId;
      result.push({
        ...summary,
        activate: () => activateWorldLoot(summary),
        bounds: objectBounds(summary, tileSize, visualSize),
        count: empty ? null : summary.slotCount,
        depth: 7,
        disabled: !travelAllowed || empty,
        disabledReason: empty
          ? `${summary.name} is empty.`
          : travelUnavailableReason,
        empty,
        execute: () => activateWorldLootNow(summary),
        id: `world-loot:${summary.containerId}`,
        imageUrl: getMapImageUrl(imageId, MEDIA_VARIANTS.TILE_DETAIL),
        kind: 'worldLoot',
        label: `${summary.name} at ${summary.x}, ${summary.y}, ${empty ? 'empty' : `${summary.slotCount} item${summary.slotCount === 1 ? '' : 's'}`}`,
        priority: OBJECT_PRIORITY.worldLoot,
        visualSize,
      });
    });

    (regionWorldState?.groundLoot || []).forEach(summary => {
      result.push({
        ...summary,
        acceptsDrop: true,
        activate: () => activateGroundLoot(summary),
        bounds: objectBounds(summary, tileSize, 44),
        count: summary.slotCount,
        depth: 6,
        disabled: !travelAllowed,
        disabledReason: travelUnavailableReason,
        execute: () => activateGroundLootNow(summary),
        id: `ground-loot:${summary.containerId}`,
        kind: 'groundLoot',
        label: `${summary.tier} treasure pack at ${summary.x}, ${summary.y}, ${summary.slotCount} item${summary.slotCount === 1 ? '' : 's'}`,
        priority: OBJECT_PRIORITY.groundLoot,
        visualSize: 44,
      });
    });

    (regionWorldState?.resourceNodes || []).forEach(node => {
      if (node.status === 'depleted') return;
      const name = resourceNodeName(node);
      const statusLabel = node.status === 'available'
        ? `available, ${node.remainingCharges} charge${node.remainingCharges === 1 ? '' : 's'} remaining`
        : node.status === 'reserved' ? 'reserved' : 'depleted';
      result.push({
        ...node,
        activate: () => activateResourceNode(node),
        bounds: objectBounds(node, tileSize, 44),
        count: node.status === 'available' ? node.remainingCharges : '!',
        depth: 5,
        disabled: !travelAllowed
          || node.status !== 'available'
          || Boolean(championBusyPlacementId),
        disabledReason: !travelAllowed
          ? travelUnavailableReason
          : node.status !== 'available'
            ? `${name} is ${node.status}.`
            : championBusyPlacementId
              ? 'A champion challenge is already starting.'
              : '',
        execute: () => activateResourceNodeNow(node),
        id: `resource:${node.placementId}`,
        imageUrl: getMapImageUrl(
          node.mapImageId,
          MEDIA_VARIANTS.ENTITY_ICON,
        ),
        kind: 'resource',
        label: `${name} resource node at ${node.x}, ${node.y}, ${statusLabel}`,
        priority: OBJECT_PRIORITY.resource,
        visuallyDisabled: false,
        visualSize: 44,
      });
    });
    return result;
  }, [
    activateChampion,
    activateChampionNow,
    activateGroundLoot,
    activateGroundLootNow,
    activateResourceNode,
    activateResourceNodeNow,
    activateStarterCache,
    activateStarterCacheNow,
    activateRoamingEnemy,
    activateWorldLoot,
    activateWorldLootNow,
    campEntrance,
    championBusyPlacementId,
    getMapImageUrl,
    movementPending,
    mapObjectImageRevision,
    onActivateCampEntrance,
    onActivateRegionExit,
    playerLevel,
    regionExits,
    regionWorldState?.champion,
    regionWorldState?.groundLoot,
    regionWorldState?.resourceNodes,
    regionWorldState?.roamingEnemies,
    regionWorldState?.worldLootContainers,
    selectTarget,
    starterCache,
    tileSize,
    travelAllowed,
    travelUnavailableReason,
  ]);

  useEffect(() => {
    if (
      queuedWorldIntent?.owner !== 'map'
      || movementPending
      || movementTarget
      || !['IDLE', 'RESTING'].includes(
        String(playerAction || '').toUpperCase(),
      )
    ) return;

    if (queuedWorldIntent.kind === 'tile') {
      const valid = queuedWorldIntent.x >= 0
        && queuedWorldIntent.y >= 0
        && queuedWorldIntent.x < map.width
        && queuedWorldIntent.y < map.height
        && isWalkable?.(queuedWorldIntent.x, queuedWorldIntent.y);
      if (!valid) {
        onClearWorldIntent?.(
          `${queuedWorldIntent.label} is no longer reachable.`,
        );
        return;
      }
      onConsumeWorldIntent?.(queuedWorldIntent.key);
      Promise.resolve(selectTarget(
        queuedWorldIntent.x,
        queuedWorldIntent.y,
      )).catch(error => {
        console.error('Failed to run the queued destination', error);
      });
      return;
    }

    const object = interactiveObjects.find(candidate => (
      candidate.id === queuedWorldIntent.id
    ));
    if (!object || object.disabled || typeof object.execute !== 'function') {
      onClearWorldIntent?.(
        `${queuedWorldIntent.label} is no longer available.`,
      );
      return;
    }
    onConsumeWorldIntent?.(queuedWorldIntent.key);
    Promise.resolve(object.execute()).catch(error => {
      console.error('Failed to run the queued map action', error);
    });
  }, [
    interactiveObjects,
    isWalkable,
    map.height,
    map.width,
    movementPending,
    movementTarget,
    onClearWorldIntent,
    onConsumeWorldIntent,
    playerAction,
    queuedWorldIntent,
    selectTarget,
  ]);

  const onlinePlayers = useMemo(() => groupOnlinePlayerMarkers(
    regionWorldState?.onlinePlayers,
  ), [regionWorldState?.onlinePlayers]);

  const dropTiles = useMemo(() => [
    [0, 0],
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ].map(([dx, dy]) => ({
    x: playerPosition.x + dx,
    y: playerPosition.y + dy,
  })).filter(({ x, y }) => (
    x >= 0 && y >= 0 && x < map.width && y < map.height
    && canDropOnTile(x, y)
  )), [
    canDropOnTile,
    map.height,
    map.width,
    playerPosition.x,
    playerPosition.y,
  ]);

  const pointFromCanvasEvent = useCallback((event, currentTransform) => (
    screenPointToTile({
      canvasRect: event.currentTarget.getBoundingClientRect(),
      clientX: event.clientX,
      clientY: event.clientY,
      mapHeight: map.height,
      mapWidth: map.width,
      tileSize,
      transform: currentTransform,
    })
  ), [map.height, map.width, tileSize]);

  const activateObject = useCallback(object => {
    if (!object) return;
    if (object.disabled) {
      setAnnouncement(object.disabledReason || 'This object is unavailable.');
      return;
    }
    Promise.resolve(object.activate?.()).catch(error => {
      console.error('Failed to activate the selected map object', error);
      setAnnouncement('That map action could not be completed.');
    });
  }, []);

  const handleCanvasClick = useCallback((event, currentTransform) => {
    if (rendererState !== 'ready') return;
    gridRef.current?.focus({ preventScroll: true });
    const point = pointFromCanvasEvent(event, currentTransform);
    if (!point) return;
    setKeyboardCursor({ x: point.x, y: point.y });
    const hit = hitTestInteractiveObjects({
      objects: interactiveObjects,
      worldX: point.worldX,
      worldY: point.worldY,
    });
    if (
      draggedItem?.fromComponent === 'inventory'
      && canDropOnTile(point.x, point.y)
      && (!hit.consumed || hit.target?.acceptsDrop === true)
    ) {
      onDropInventoryItem?.(draggedItem, { x: point.x, y: point.y });
      setAnnouncement(`Drop destination selected at ${point.x}, ${point.y}.`);
      return;
    }
    if (hit.consumed) {
      activateObject(hit.target);
      return;
    }
    if (!travelAllowed) {
      setAnnouncement(travelUnavailableReason);
      return;
    }
    selectDestination(point.x, point.y);
  }, [
    activateObject,
    canDropOnTile,
    draggedItem,
    interactiveObjects,
    onDropInventoryItem,
    pointFromCanvasEvent,
    rendererState,
    selectDestination,
    travelAllowed,
    travelUnavailableReason,
  ]);

  const handleCanvasPointerMove = useCallback((event, currentTransform) => {
    const point = pointFromCanvasEvent(event, currentTransform);
    const target = point ? hitTestInteractiveObjects({
      objects: interactiveObjects,
      worldX: point.worldX,
      worldY: point.worldY,
    }).target : null;
    setHoveredObjectId(current => (
      current === target?.id ? current : target?.id || null
    ));
  }, [interactiveObjects, pointFromCanvasEvent]);

  const canDropAtCanvasPoint = useCallback(point => {
    if (!point || !canDropOnTile(point.x, point.y)) return false;
    const hit = hitTestInteractiveObjects({
      objects: interactiveObjects,
      worldX: point.worldX,
      worldY: point.worldY,
    });
    return !hit.consumed || hit.target?.acceptsDrop === true;
  }, [canDropOnTile, interactiveObjects]);

  const handleCanvasDragOver = useCallback((event, currentTransform) => {
    if (canDropAtCanvasPoint(pointFromCanvasEvent(event, currentTransform))) {
      event.preventDefault();
    }
  }, [canDropAtCanvasPoint, pointFromCanvasEvent]);

  const handleCanvasDrop = useCallback((event, currentTransform) => {
    const point = pointFromCanvasEvent(event, currentTransform);
    if (!canDropAtCanvasPoint(point)) return;
    event.preventDefault();
    event.stopPropagation();
    onDropInventoryItem?.(draggedItem, { x: point.x, y: point.y });
  }, [
    canDropAtCanvasPoint,
    draggedItem,
    onDropInventoryItem,
    pointFromCanvasEvent,
  ]);

  const handleMapKeyDown = useCallback(event => {
    if (rendererState !== 'ready') return;
    if (event.key.startsWith('Arrow')) {
      event.preventDefault();
      const nextCursor = moveKeyboardCursor(
        keyboardCursor,
        event.key,
        map.width,
        map.height,
      );
      setKeyboardCursor(nextCursor);
      setCameraOffset(currentOffset => calculateCameraOffset({
        currentOffset,
        mapHeight: map.height,
        mapWidth: map.width,
        playerPosition: nextCursor,
        tileSize,
        viewportHeight: viewport.height,
        viewportWidth: viewport.width,
      }));
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const blockedDropTarget = interactiveObjects.some(object => (
        object.x === keyboardCursor.x
        && object.y === keyboardCursor.y
        && object.acceptsDrop !== true
      ));
      if (
        draggedItem?.fromComponent === 'inventory'
        && canDropOnTile(keyboardCursor.x, keyboardCursor.y)
        && !blockedDropTarget
      ) {
        onDropInventoryItem?.(draggedItem, keyboardCursor);
        setAnnouncement(
          `Drop destination selected at ${keyboardCursor.x}, ${keyboardCursor.y}.`,
        );
        return;
      }
      if (!travelAllowed) {
        setAnnouncement(travelUnavailableReason);
        return;
      }
      selectDestination(keyboardCursor.x, keyboardCursor.y);
    }
  }, [
    canDropOnTile,
    draggedItem,
    interactiveObjects,
    keyboardCursor,
    map.height,
    map.width,
    onDropInventoryItem,
    rendererState,
    selectDestination,
    tileSize,
    viewport.height,
    viewport.width,
    travelAllowed,
    travelUnavailableReason,
  ]);

  const retryRenderer = useCallback(() => {
    setRendererComponent(null);
    setRendererError('');
    setRendererState(supportsWebGL2() ? 'loading' : 'unsupported');
    setRendererKey(key => key + 1);
  }, []);

  const activeTileLabel = `Tile ${keyboardCursor.x}, ${keyboardCursor.y}${playerPosition.x === keyboardCursor.x && playerPosition.y === keyboardCursor.y ? ', player location' : ''}${movementTarget?.x === keyboardCursor.x && movementTarget?.y === keyboardCursor.y ? ', movement target' : ''}`;
  const mapLabel = travelAllowed
    ? 'Region map. Use arrow keys to choose a tile and Enter to select it. Tab to reach map objects.'
    : `Region map. ${travelUnavailableReason}`;

  return (
    <div
      className="tilemap__viewport"
      style={{ '--map-movement-step-duration': `${MOVEMENT_STEP_DURATION_MS}ms` }}
    >
      <div className="tilemap__stage" ref={stageRef}>
        <div
          aria-activedescendant={rendererState === 'ready'
            ? `game-map-tile-${keyboardCursor.y}-${keyboardCursor.x}`
            : undefined}
          aria-busy={rendererState === 'loading' || undefined}
          aria-colcount={map.width}
          aria-disabled={!travelAllowed || rendererState !== 'ready' || undefined}
          aria-label={mapLabel}
          aria-rowcount={map.height}
          className="tilemap__grid"
          onKeyDown={handleMapKeyDown}
          ref={gridRef}
          role="grid"
          tabIndex={rendererState === 'ready' ? 0 : -1}
        >
          {RendererComponent ? (
            <RendererComponent
              avatarSelection={avatarSelection}
              cameraTiltDegrees={CAMERA_TILT_DEGREES}
              details={visibleDetails}
              dropTiles={dropTiles}
              focusedObjectId={focusedObjectId}
              height={viewport.height || 1}
              hoveredObjectId={hoveredObjectId}
              key={rendererKey}
              keyboardCursor={keyboardCursor}
              movementPath={movementPath}
              movementTarget={movementTarget}
              queuedDestination={
                queuedWorldIntent?.owner === 'map'
                && queuedWorldIntent.kind === 'tile'
                  ? queuedWorldIntent
                  : null
              }
              selectedObjectId={
                queuedWorldIntent
                && queuedWorldIntent.kind !== 'tile'
                  ? queuedWorldIntent.id
                  : null
              }
              objects={interactiveObjects}
              onlinePlayers={onlinePlayers}
              onCanvasClick={handleCanvasClick}
              onCanvasDragOver={handleCanvasDragOver}
              onCanvasDrop={handleCanvasDrop}
              onCanvasPointerLeave={() => setHoveredObjectId(null)}
              onCanvasPointerMove={handleCanvasPointerMove}
              onContextLost={() => {
                setRendererError('The graphics context was lost.');
                setRendererState('error');
              }}
              onContextRestored={retryRenderer}
              onError={error => {
                setRendererError(error?.message || 'The renderer could not start.');
                setRendererState('error');
              }}
              onReady={() => setRendererState('ready')}
              playerAction={playerAction}
              playerPosition={playerPosition}
              reducedMotion={reducedMotion}
              scavengeRadius={scavengeRadius}
              spritePitchDegrees={WORLD_SPRITE_PITCH_DEGREES}
              terrainRows={terrainRows}
              tileSize={tileSize}
              transform={transform}
              width={viewport.width || 1}
            />
          ) : null}
          <div className="tilemap__semantic-row" role="row">
            <span
              aria-label={activeTileLabel}
              aria-selected="true"
              className="tilemap__semantic-cell"
              id={`game-map-tile-${keyboardCursor.y}-${keyboardCursor.x}`}
              role="gridcell"
            />
          </div>
        </div>

        <div
          aria-label="Shared region objects"
          className="tilemap__semantic-objects"
        >
          {interactiveObjects.map(object => (
            <button
              aria-busy={object.gathering || undefined}
              aria-label={`${object.label}${queuedWorldIntent?.id === object.id ? ', selected as next action' : ''}`}
              aria-pressed={queuedWorldIntent?.id === object.id}
              className={`tilemap__semantic-object tilemap__semantic-object--${object.kind}`}
              data-map-object-id={object.id}
              disabled={object.disabled || rendererState !== 'ready'}
              key={object.id}
              onBlur={() => setFocusedObjectId(current => (
                current === object.id ? null : current
              ))}
              onClick={() => activateObject(object)}
              onFocus={() => setFocusedObjectId(object.id)}
              type="button"
            />
          ))}
        </div>

        <div className="tilemap__online-player-list">
          {onlinePlayers.map(player => {
            const action = String(player.currentAction || 'IDLE').toLowerCase();
            const crowdLabel = player.hiddenCount > 0
              ? `, ${player.hiddenCount} more player${player.hiddenCount === 1 ? '' : 's'} on this tile`
              : '';
            return (
              <span
                aria-label={`${player.name}, ${action} at tile ${player.x}, ${player.y}${crowdLabel}`}
                key={`${player.name}:${player.x}:${player.y}`}
                role="img"
              />
            );
          })}
        </div>

        {rendererState !== 'ready' ? (
          <div
            className="tilemap__renderer-message"
            role={rendererState === 'loading' ? 'status' : 'alert'}
          >
            <strong>
              {rendererState === 'loading'
                ? 'Preparing the world…'
                : rendererState === 'unsupported'
                  ? 'This browser cannot render the world map because WebGL 2 is unavailable. Try a current browser or device.'
                  : 'The world renderer stopped.'}
            </strong>
            {rendererError ? <span>{rendererError}</span> : null}
            {rendererState !== 'loading' ? (
              <button onClick={retryRenderer} type="button">
                Retry renderer
              </button>
            ) : null}
          </div>
        ) : null}

        <div aria-live="polite" className="tilemap__live-region">
          {announcement}
        </div>
      </div>
    </div>
  );
}
